/**
 * Client-side image compression + format conversion before upload.
 *
 * Why this exists:
 *   • Our backend caps uploads at 2 MB, but iPhone photos are routinely
 *     3–5 MB — mobile users were hitting silent 413/400 errors with no
 *     clear path forward.
 *   • iOS defaults to HEIC, which Chrome / Firefox cannot decode. Safari
 *     (where the user hitting this bug is) CAN decode HEIC in canvas, so
 *     routing every mobile upload through this helper converts HEIC → JPEG
 *     at the browser.
 *   • Downscaling to a sane dimension (1600 px long edge) is enough for
 *     a player headshot / team photo and keeps the final file well under
 *     the 2 MB cap even at high JPEG quality.
 */

const DEFAULTS = {
  maxBytes: 1.8 * 1024 * 1024, // leave headroom under the 2 MB server cap
  maxDim: 1600,                // longest edge in pixels
  startQuality: 0.85,
  minQuality: 0.5,
  outputType: 'image/jpeg',
};

/**
 * Load a File as an HTMLImageElement via an object URL.
 * Throws if the browser can't decode the format (e.g., HEIC on desktop).
 */
async function _loadImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    // img.decode() rejects when the browser can't decode the bitmap
    // (HEIC on Chrome, broken files, etc.) — the caller surfaces this
    // as a human-readable "format not supported" message.
    await img.decode();
    return img;
  } finally {
    // Revoke slightly late so the <img> keeps a reference while rendering;
    // Safari dislikes an eager revoke before draw. 1-tick timeout is enough.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

async function _canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Compress a user-selected image to fit under the server's 2 MB cap and
 * convert any HEIC/HEIF variant to JPEG so it round-trips cleanly.
 *
 * Returns a NEW File (same base name, `.jpg` extension) ready for upload.
 * Caller is expected to wrap this in try/catch and surface the error.
 */
export async function compressImageForUpload(file, overrides = {}) {
  const opts = { ...DEFAULTS, ...overrides };

  // Guard: iCloud lazy-loaded photos can arrive from the picker as a
  // 0-byte File when the actual bits haven't been downloaded yet. Catch
  // that NOW with a clear message instead of silently producing an
  // empty canvas and letting the server reject it as "field required".
  if (!file || file.size === 0) {
    throw new Error(
      "This photo hasn't finished downloading from iCloud. Open it in Photos, wait for it to fully load, then try again."
    );
  }

  // Fast path — if it's already a small JPEG/PNG under the cap, skip work.
  const alreadySmallEnough =
    file.size <= opts.maxBytes &&
    /^image\/(jpeg|png)$/i.test(file.type);
  if (alreadySmallEnough) return file;

  const img = await _loadImage(file);
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = longest > opts.maxDim ? opts.maxDim / longest : 1;
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // White background in case the source has transparency and we're
  // emitting JPEG (which can't carry alpha).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  // Step quality down until the blob fits under the cap. Stop at
  // minQuality to avoid producing visibly garbage output.
  let quality = opts.startQuality;
  let blob = await _canvasToBlob(canvas, opts.outputType, quality);
  while (blob && blob.size > opts.maxBytes && quality > opts.minQuality) {
    quality = Math.max(opts.minQuality, quality - 0.1);
    blob = await _canvasToBlob(canvas, opts.outputType, quality);
  }
  if (!blob || blob.size === 0) {
    throw new Error('Could not process this image. Please pick a different photo.');
  }

  const baseName = (file.name || 'photo').replace(/\.(heic|heif|jpg|jpeg|png|gif|webp)$/i, '');
  const out = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  // Belt-and-suspenders: the File() constructor on iOS WebKit has been
  // known to produce a File whose `.size` reports non-zero but reads 0
  // bytes through FormData. Verifying both the Blob and the final File
  // report size>0 at this layer catches that before we ship it.
  if (out.size === 0) {
    throw new Error('Could not process this image. Please pick a different photo.');
  }
  return out;
}


/**
 * POST an image to an API endpoint with iOS-safe retry semantics.
 *
 * Why this helper exists:
 *   On iOS WebKit (every iPhone browser incl. Brave, Chrome iOS, Firefox
 *   iOS, Safari) two separate issues can cause the server to receive an
 *   empty multipart envelope and return 422 "field required":
 *     1. The page's service worker re-issues the POST via `fetch(req)`,
 *        dropping the body. (Fixed in /public/sw.js — but users with the
 *        OLD SW still cached need a retry path right now.)
 *     2. A File reconstructed from a canvas Blob can report size>0 but
 *        read as 0 bytes through FormData if the browser GC'd the blob
 *        between creation and send.
 *
 *   When the server comes back with a 422 we try ONE automatic retry
 *   using the ORIGINAL file (bypassing the canvas pipeline) — this
 *   handles both paths without the user having to tap again.
 *
 * @param {string} apiUrl   REACT_APP_BACKEND_URL
 * @param {string} endpoint  e.g. '/api/upload/headshot'
 * @param {File}   original  The File from the <input type=file> picker
 * @param {string} token     Optional bearer token
 * @returns {Promise<{storage_path: string}>} on success
 * @throws Error with user-safe message on failure
 */
export async function uploadImageWithRetry(apiUrl, endpoint, original, token) {
  if (!original || original.size === 0) {
    throw new Error(
      "This photo hasn't finished downloading from iCloud. Open it in Photos, wait for it to fully load, then try again."
    );
  }

  // First attempt — run through the canvas compression pipeline so iPhone
  // HEIC photos get converted + downscaled and 5 MB camera-roll JPEGs are
  // trimmed under the server's 2 MB cap.
  let compressed;
  try {
    compressed = await compressImageForUpload(original);
  } catch (e) {
    // Compression failure is a dead-end for THIS pipeline, but the raw
    // file might still be fine (backend accepts HEIC up to 10 MB via
    // pillow-heif). Fall through to the raw send below.
    compressed = null;
  }

  const _send = async (fileToSend) => {
    const fd = new FormData();
    const name = fileToSend.name || 'photo.jpg';
    fd.append('file', fileToSend, name);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${apiUrl}${endpoint}`, {
      method: 'POST', headers, body: fd,
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  // Pick the best candidate for the first attempt (compressed if present
  // and non-empty, else the original file).
  const firstAttempt = (compressed && compressed.size > 0) ? compressed : original;
  const first = await _send(firstAttempt);

  if (first.res.ok) return first.data;

  // Signature of the SW-drops-body / blob-detachment bug: 422 with
  // `field required` on the `file` field. Retry ONCE with the raw file.
  const detail = first.data && first.data.detail;
  const looksMissing =
    first.res.status === 422 &&
    (
      (typeof detail === 'string' && /required/i.test(detail)) ||
      (Array.isArray(detail) && detail.some(d =>
        d && typeof d.msg === 'string' && /required/i.test(d.msg)
      ))
    );

  // Avoid an infinite loop — only retry if the first attempt wasn't
  // already the raw file.
  if (looksMissing && firstAttempt !== original) {
    const second = await _send(original);
    if (second.res.ok) return second.data;
    // Second attempt failed — fall through to generic error below.
    first.res = second.res; first.data = second.data;
  }

  // If BOTH attempts came back with the "body dropped" signature, the
  // page is almost certainly running an old service worker whose fetch
  // handler re-serializes POSTs (iOS WebKit drops the body in that path).
  // Nuke every registered SW so the next refresh hits the network
  // directly, and surface a crystal-clear instruction to the user.
  const detailFinal = first.data && first.data.detail;
  const stillMissing =
    first.res.status === 422 &&
    (
      (typeof detailFinal === 'string' && /required/i.test(detailFinal)) ||
      (Array.isArray(detailFinal) && detailFinal.some(d =>
        d && typeof d.msg === 'string' && /required/i.test(d.msg)
      ))
    );
  if (stillMissing && typeof navigator !== 'undefined'
      && navigator.serviceWorker && navigator.serviceWorker.controller) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    } catch (_e) { /* ignore — we're about to throw a helpful error */ }
    throw new Error(
      "A cached helper on this browser blocked the upload. We've cleared it — please pull down to refresh and try again."
    );
  }

  // Non-retryable failure. Throw an Error whose message either carries
  // the server's detail (prefers pydantic 'msg') or a fallback.
  const detail2 = first.data && first.data.detail;
  let msg = 'Upload failed';
  if (typeof detail2 === 'string') msg = detail2;
  else if (Array.isArray(detail2) && detail2[0] && typeof detail2[0].msg === 'string') {
    msg = detail2[0].msg;
  } else if (first.data && typeof first.data.message === 'string') {
    msg = first.data.message;
  }
  const err = new Error(msg);
  err.status = first.res.status;
  throw err;
}

